import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { fetchAiExecutiveSummary } from '../../api/ai';
import type { AiExecutiveSummary } from '../../types';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { getBackendError } from '../../lib/utils';

/**
 * AI Yönetici Özeti kartı (dashboard sağ kolon). Kota tasarrufu için özet
 * yalnız kullanıcı butona bastığında yüklenir (10 istek/saat limiti).
 * Backend sabit rol kapılı: admin/yonetici/sef.
 */
export function AiExecutiveSummaryCard({ projectId }: { projectId?: number }) {
  const [summary, setSummary] = useState<AiExecutiveSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!projectId) return null;

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    try {
      setSummary(await fetchAiExecutiveSummary(projectId));
    } catch (err) {
      setError(getBackendError(err) || 'Yönetici özeti oluşturulamadı.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ai-exec-summary-card">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <Sparkles className="h-4 w-4 text-violet-500" />
          AI Yönetici Özeti
        </div>
        <button
          type="button"
          onClick={handleGenerate}
          disabled={loading}
          data-testid="ai-exec-summary-generate"
          className="rounded-lg bg-violet-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-violet-700 disabled:opacity-50"
        >
          {loading ? 'Oluşturuluyor…' : summary ? 'Yenile' : 'Özet Oluştur'}
        </button>
      </div>

      {loading && (
        <div className="flex justify-center py-4">
          <LoadingSpinner />
        </div>
      )}
      {error && (
        <div className="rounded-lg bg-red-50 p-2 text-xs text-red-700" data-testid="ai-exec-summary-error">
          {error}
        </div>
      )}
      {!loading && !summary && !error && (
        <p className="text-xs text-slate-400" data-testid="ai-exec-summary-hint">
          Son 7 günün görev, rapor ve İSG metriklerinden Türkçe yönetici özeti üretir.
        </p>
      )}
      {summary && !loading && (
        <div data-testid="ai-exec-summary-result">
          <p className="whitespace-pre-wrap text-sm text-slate-700">{summary.summary}</p>
          {summary.highlights.length > 0 && (
            <ul className="mt-2 list-disc space-y-0.5 pl-4 text-xs text-slate-500">
              {summary.highlights.map((highlight, i) => (
                <li key={i}>{highlight}</li>
              ))}
            </ul>
          )}
          <div className="mt-2 text-[10px] text-slate-400">
            {summary.period.from} → {summary.period.to}
            {summary.ai_status === 'fallback' ? ' · kural tabanlı özet' : ''}
          </div>
        </div>
      )}
    </div>
  );
}
